import React, { useRef, useEffect } from "react";
import * as d3 from "d3";

export interface MarketPriceData {
  price: number;
  name: string;
  selected?: boolean;
}
interface MarketPriceChartProps {
  data: MarketPriceData[];
  svgWidth?: number;
  svgHeight?: number;
  price: number;
}

const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
});

interface ChartConfig {
  data: MarketPriceData[];
  selector: string;
  screenWidth: number;
  svgWidth?: number;
  svgHeight?: number;
  price: number;
}

export function initializeMarketPriceChart(config: ChartConfig) {
  const {
    selector,
    data: paramData,
    screenWidth,
    svgWidth: SVG_WIDTH = 400,
    svgHeight: SVG_HEIGHT = 100,
    price,
  } = config;
  const data = [...paramData];
  const fontClass = "text-[14px] sm:text-[10px] text-[#354360]";
  data.sort((a, b) => a.price - b.price);
  const MARGIN = { LEFT: 0, RIGHT: 0, TOP: 0, BOTTOM: 0 };
  const WIDTH = SVG_WIDTH - MARGIN.LEFT - MARGIN.RIGHT;
  const HEIGHT = SVG_HEIGHT - MARGIN.TOP - MARGIN.BOTTOM;
  const CHIP_HEIGHT = 0;
  const X_AXIS = HEIGHT / 2 + CHIP_HEIGHT / 2;
  const LINE_HEIGHT = 30;
  const PRICE_Y = X_AXIS + LINE_HEIGHT;
  const MIN_INNER_PADDING = SVG_WIDTH * (screenWidth > 480 ? 0.18 : 0.25);
  const MAX_INNER_PADDING = SVG_WIDTH * 0.348;

  const svg = d3
    .select(`${selector}`)
    .append("svg")
    .attr("class", "w-full h-[auto]")
    .attr("viewBox", `0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`);

  const g = svg
    .append("g")
    .attr("transform", `translate(${MARGIN.LEFT}, ${MARGIN.TOP})`);

  const min = d3.min(data.map((e) => e.price))!;
  const max = d3.max(data.map((e) => e.price))!;

  const getPaddedX = (prev: number, current: number) => {
    const diff = current - prev;
    if (diff < MIN_INNER_PADDING) {
      return current + (MIN_INNER_PADDING - diff);
    }
    if (diff > MAX_INNER_PADDING) {
      return prev + MAX_INNER_PADDING;
    }
    return current;
  };

  const xScale = d3.scaleLinear().domain([min, max]).range([0, WIDTH]).nice();

  const xDomain = xScale.domain();
  const xRange = xDomain[1] - xDomain[0];
  const padding = xRange * 0.11; // 10% padding on both ends
  const maxRight = xScale(xDomain[1] - padding);
  const minRight = xScale(xDomain[0] + padding);
  xScale.domain([xDomain[0] - padding, xDomain[1] + padding]);

  const customXValues = data.reduce((acc, d, i) => {
    let x = xScale(d.price);
    if (i > 0 && !d.selected) {
      x = getPaddedX(acc[i - 1], x);
    }
    if (x > maxRight) {
      x = maxRight;
    } else if (x < minRight) {
      x = minRight;
    }
    return {
      ...acc,
      [i]: x,
    };
  }, {} as Record<number, number>);

  const mapX = (d: MarketPriceData, i: number) => customXValues[i];

  const selectedPriceIndex = data.findIndex((e) => e.selected);

  const xAxisCall = d3
    .axisBottom(xScale)
    .tickSize(0)
    .tickFormat(() => "");

  const xAxisGroup = g
    .append("g")
    .attr("class", "x-axis")
    .attr("transform", `translate(0, ${X_AXIS})`)
    .call(xAxisCall);

  // xAxisGroup.select('.domain').attr('stroke', '#354360').attr('strokeWidth', 1);
  const defs = svg.append("defs");

  const gradient = defs
    .append("linearGradient")
    .attr("id", "gradient")
    .attr("gradientTransform", "rotate(117)");

  gradient.append("stop").attr("offset", "0%").attr("stop-color", "#53E8BB");

  gradient.append("stop").attr("offset", "100%").attr("stop-color", "#8CDDF0");

  // Append the full-width rectangle with border radius
  g.append("rect")
    .attr("x", 0)
    .attr("y", X_AXIS - 3)
    .attr("width", WIDTH)
    .attr("height", 6)
    .attr("rx", 4) // Rounded corners
    .attr("ry", 4)
    .attr("fill", "#354360");

  // Overlay the gradient rectangle for the 2/3 portion, without border radius on the left
  g.append("rect")
    .attr("x", WIDTH / 3)
    .attr("y", X_AXIS - 3)
    .attr("width", (WIDTH * 2) / 3)
    .attr("height", 6)
    .attr("rx", 4) // No rounded corner on the left side
    .attr("ry", 4)
    .attr("fill", "url(#gradient)");

  // illusion that hides the rounded in the middle
  g.append("rect")
    .attr("x", WIDTH / 3)
    .attr("y", X_AXIS - 3)
    .attr("width", 20)
    .attr("height", 6)
    .attr("rx", 0) // No rounded corner on the left side
    .attr("ry", 4)
    .attr("fill", "url(#gradient)");

  // Append a line element to each data point
  g.selectAll(".data-line")
    .data(data)
    .enter()
    .append("line")
    .attr("class", (d) =>
      ["data-line", d.selected && "your-price"].filter(Boolean).join(" ")
    )
    .attr("x1", mapX)
    .attr("y1", X_AXIS - 5)
    .attr("x2", mapX)
    .attr("y2", (d) => (d.selected ? X_AXIS : PRICE_Y) - 20) // Conditionally set the y2 position
    .attr("stroke", (d) => (d.selected ? "none" : "#354360")) // Hide stroke for selected
    .attr("strokeWidth", (d) => (d.selected ? "0px" : "1px")); // Set strokeWidth to 0 for selected

  g.selectAll("circle")
    .data(data.filter((d) => d.selected)) // Filter to only include the selected data point
    .enter()
    .append("circle")
    .attr("cx", () => customXValues[selectedPriceIndex])
    .attr("cy", () => X_AXIS)
    .attr("r", 5)
    .attr("class", (d) =>
      ["data-circle", d.selected && "your-price"].filter(Boolean).join(" ")
    )
    .attr("stroke", (d) => (d.selected ? "#00b3de" : "#354360"))
    .attr("strokeWidth", (d) => (d.selected ? "2px" : "1px"))
    .attr("fill", "#eff0f6");

  // Append an outer circle for the selected data point
  g.selectAll(".outer-circle")
    .data(data.filter((d) => d.selected)) // Filter to only include the selected data point
    .enter()
    .append("circle")
    .attr("cx", () => customXValues[selectedPriceIndex])
    .attr("cy", X_AXIS)
    .attr("r", 8) // Set a larger radius for the outer circle
    .attr("fill", "rgba(0, 179, 222, 0.20)") // Set the fill with transparency
    .attr("stroke", "none");

  const labelGroups = g
    .selectAll(".label")
    .data(data)
    .enter()
    .append("g")
    .attr("class", (d) =>
      ["data-label", d.selected && "your-price"].filter(Boolean).join(" ")
    )
    .attr("transform", (d, i) => {
      const x = customXValues[i];
      if (d.selected) {
        return `translate(${x}, ${X_AXIS - 30})`;
      }
      return `translate(${x}, ${X_AXIS + 25})`;
    })
    .attr("text-anchor", "middle")
    .attr("fill", (d) => (d.selected ? "#24a5ca" : "#354360"));

  const fontSize = screenWidth > 480 ? "10" : "14";
  labelGroups
    .append("text")
    .attr("class", `name ${fontClass}`)
    .text((d) => `${d.name}`)
    .attr("font-size", fontSize)
    .attr("x", 0);

  labelGroups
    .append("text")
    .attr("class", `price ${fontClass}`)
    .text((d) => formatter.format(d.price).slice(0, -3))
    .attr("font-size", fontSize)
    .attr("y", 16)
    .attr("font-weight", "700");
}

export function destroySvgChart(selector: string) {
  d3.select(`${selector}`)?.select("svg").remove();
}

const MarketPriceChart: React.FC<MarketPriceChartProps> = ({
  data,
  svgWidth = 400,
  svgHeight = 100,
  price,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const screenWidth = window.innerWidth;

  useEffect(() => {
    // Check if ref.current is defined and the data is not empty
    if (!ref.current || data.length === 0) return;

    const config = {
      selector: `#${ref.current.id}`,
      data,
      screenWidth,
      svgWidth,
      svgHeight,
      price,
    };

    initializeMarketPriceChart(config);

    // Return a cleanup function to remove the SVG when the component is unmounted
    return () => destroySvgChart(`#${ref.current?.id}`);
  }, [data, price]);

  const uniqueId = `d3-svg-${Date.now()}`;

  return (
    <div
      className="w-full relative group lg:px-8"
      id={uniqueId}
      ref={ref}
    ></div>
  );
};

export default MarketPriceChart;
